// How a Facebook FEED post lays out several attached photos — the composer
// preview's collage (round 2 item 1). Pure, so every rule is asserted without
// jsdom (the linkCard.ts posture).
//
// ⚠️⚠️ THIS IS A PREDICTION, NOT A SPEC OF FACEBOOK. Facebook's real collage
// varies with each image's aspect ratio (a set of portrait photos lays out
// differently from landscape ones) and differs between web and the app. This
// models the common shape so the preview stops showing one photo when the post
// will show four. Do not "fix" it to match one particular Facebook render — the
// next render will differ.
//
// RULES:
//   1 photo  → the single photo, natural ratio (unchanged)
//   2        → two equal tiles side by side
//   3        → one tall tile left, two stacked right
//   4        → 2×2
//   5+       → 2×2, the fourth tile dimmed with "+N" over it (N = hidden count)
// ⚠️ The badge threshold is FLAGGED for Scott: he wrote "until there are 4+
// then it has + more"; Facebook shows all four at exactly four, so this starts
// the badge at five. VISIBLE_BEFORE_BADGE is the one constant to change if he
// wants the badge from four.
// - FEED photos only. A Reel is a single 9:16 surface, a Story is media-only,
//   and a post with any video in it does not collage — those keep the single
//   item preview.
// - ORDER comes from the media array (drag-reorderable since round 1 item 3):
//   the first item is the large tile.

import type { PostMedia } from '../../types/publishing';

/** The most tiles a collage shows. Above this the last tile carries "+N". */
const MAX_TILES = 4;
/**
 * Tiles shown without an overflow badge. 4 = Facebook's behaviour (all four at
 * exactly four). Set to 3 to badge from four, if that is what Scott wants.
 */
const VISIBLE_BEFORE_BADGE = 4;

export function facebookCollage(count: number): { tiles: number; overflow: number } {
  const n = Math.max(0, Math.floor(count));
  if (n <= VISIBLE_BEFORE_BADGE) return { tiles: Math.min(n, MAX_TILES), overflow: 0 };
  // The badge counts what is NOT shown as its own photo: everything past the
  // tiles shown un-dimmed. At VISIBLE_BEFORE_BADGE = 4 that is count - 4.
  return { tiles: MAX_TILES, overflow: n - VISIBLE_BEFORE_BADGE };
}

/** Does this post's media collage at all? Feed posts with 2+ media, all photos. */
export function facebookCollageApplies(
  media: readonly Pick<PostMedia, 'type'>[],
  contentType: 'feed' | 'reel' | 'story',
): boolean {
  return contentType === 'feed' && media.length >= 2 && media.every((m) => m.type === 'image');
}

/** The media shown as tiles, in array order — the first item is the large tile. */
export function collageTiles<T>(media: readonly T[]): { tiles: T[]; overflow: number } {
  const { tiles, overflow } = facebookCollage(media.length);
  return { tiles: media.slice(0, tiles), overflow };
}
